import React from 'react';
import { useSelector } from 'react-redux';
import { useStyles } from './styles';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Chip,
    Tooltip,
    IconButton,
    Collapse,
    Box,
    Typography,
    Grid,
    Popover,
    Modal,
    Button,
    useTheme
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, ZoomIn, Analytics, Code } from '@mui/icons-material';
import { IStore } from '../../store/types';
import dayjs from 'dayjs';

interface RemovebgLog {
    eventId: number;
    status: string;
    additionalEvents: number;
    mockupUrl: string;
    imageUrl: string;
    toCart: number;
    orderExist: number;
    ip: string;
    customerId: number;
    sessionId: number;
    createdAt: string;
    errorMessage: string | null;
    errorMessageDetailed: string | null;
    imageType: string;
    initMockupDelay: string;
    processDuration: string;
    mockupDuration: string;
    actualMockupDuration: string;
    saveDuration: string;
    skipSave: number;
    totalDurationMs: number | null;
    inferenceMs: number | null;
    postprocessMs: number | null;
    targetResolution: string | null;
    isRetryExist: boolean | null;
    log: any;
}

interface RowProps {
    log: RemovebgLog;
}

function Row({ log }: RowProps) {
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const [magnifierAnchor, setMagnifierAnchor] = React.useState<HTMLElement | null>(null);
    const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
    const [detailedLogOpen, setDetailedLogOpen] = React.useState(false);
    const [rawDataOpen, setRawDataOpen] = React.useState(false);
    const classes = useStyles();

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'OK': return 'success';
            case 'ERROR': return 'error';
            default: return 'default';
        }
    };

    const formatDuration = (duration: string | number | null) => {
        if (duration === null || duration === undefined) return '-';
        if (typeof duration === 'string') return `${duration}s`;
        return `${duration}ms`;
    };

    const formatResolution = (resolution: string | null) => {
        if (!resolution) return '-';
        return `${resolution}x${resolution}`;
    };

    const handleMagnifierClick = (event: React.MouseEvent<HTMLElement>) => {
        setMagnifierAnchor(event.currentTarget);
    };

    const handleMagnifierClose = () => {
        setMagnifierAnchor(null);
    };

    const handleImageMouseMove = (event: React.MouseEvent<HTMLImageElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
    };

    const magnifierOpen = Boolean(magnifierAnchor);

    const getQualityColor = (score: number) => {
        if (score >= 0.8) return '#4caf50';
        if (score >= 0.6) return '#ff9800';
        return '#f44336';
    };

    const getQualityLabel = (score: number) => {
        if (score >= 0.8) return 'Excellent';
        if (score >= 0.6) return 'Good';
        return 'Poor';
    };

    const calculateEdgeQuality = (logData: any) => {
        if (!logData.enhanced_edge_quality_1024) {
            return logData.edge_quality_stats_1024?.quality_score || 0;
        }

        const enhanced = logData.enhanced_edge_quality_1024;
        
        // Базові метрики якості країв
        const edgeSharpness = enhanced.edge_sharpness || 0;
        const colorConsistency = enhanced.color_consistency_score || 0;
        const morphologicalCleanness = enhanced.morphological_cleanness || 0;
        
        // Додаткові метрики
        const cannyEdgeDensity = enhanced.canny_edge_density || 0;
        const sobelEdgeStrength = Math.min((enhanced.sobel_edge_strength || 0) / 30, 1); // Нормалізуємо до 0-1
        const colorBleedingRatio = enhanced.color_bleeding_ratio || 0;
        const weightedArtifactScore = enhanced.weighted_artifact_score || 0;
        
        // Розрахунок загальної якості країв
        // Ваги для різних метрик
        const weights = {
            sharpness: 0.25,
            colorConsistency: 0.20,
            morphologicalCleanness: 0.20,
            edgeDensity: 0.15,
            edgeStrength: 0.10,
            artifactScore: 0.10
        };
        
        // Штрафи за проблеми
        const colorBleedingPenalty = colorBleedingRatio * 0.3; // Штраф за протікання кольору
        
        // Розрахунок зваженої суми
        const weightedScore = 
            edgeSharpness * weights.sharpness +
            colorConsistency * weights.colorConsistency +
            morphologicalCleanness * weights.morphologicalCleanness +
            cannyEdgeDensity * weights.edgeDensity +
            sobelEdgeStrength * weights.edgeStrength +
            weightedArtifactScore * weights.artifactScore;
        
        // Застосування штрафів
        const finalScore = Math.max(0, Math.min(1, weightedScore - colorBleedingPenalty));
        
        return finalScore;
    };

    const calculateProcessingTimes = (logData: any) => {
        // Базові часи обробки (без ретраю)
        const basePreprocess = logData.preprocess_ms || 0;
        
        // Якщо є ретрай, показуємо часи ретраю як основні
        if (logData.retry_performed && logData.retry_stats) {
            const retryPreprocess = logData.retry_stats.preprocess_time_ms || 0;
            const retryAnalysis = logData.retry_stats.analysis_time_ms || 0;
            const retryBgRemoval = logData.retry_stats.bg_removal_time_ms || 0;
            const retryPostprocess = logData.retry_stats.postprocess_time_ms || 0;
            
            return {
                preprocess: basePreprocess + retryPreprocess,
                analysis: retryAnalysis,
                bgRemoval: retryBgRemoval,
                postprocess: retryPostprocess
            };
        }
        
        // Якщо ретраю немає, повертаємо тільки preprocess (інші часи не доступні)
        return {
            preprocess: basePreprocess,
            analysis: 0,
            bgRemoval: 0,
            postprocess: 0
        };
    };

    const renderCompactLogData = (logData: any) => {
        if (!logData) return null;

        const renderProgressBar = (value: number, max: number = 1, label: string, color?: string, tooltip?: string) => (
            <Tooltip title={tooltip || `${label}: ${((value || 0) * 100).toFixed(1)}%`} arrow>
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                            {((value || 0) * 100).toFixed(1)}%
                        </Typography>
                    </Box>
                    <Box sx={{
                        width: '100%',
                        height: '6px',
                        bgcolor: theme.palette.action.hover,
                        borderRadius: '3px',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            width: `${((value || 0) / max) * 100}%`,
                            height: '100%',
                            bgcolor: color || getQualityColor(value || 0),
                            transition: 'width 0.3s ease'
                        }} />
                    </Box>
                </Box>
            </Tooltip>
        );

        // Calculate total processing time safely
        const processingTimes = calculateProcessingTimes(logData);
        const totalTime = processingTimes.preprocess + processingTimes.analysis + processingTimes.bgRemoval + processingTimes.postprocess;

        // Get overall quality for color coding
        const overallQuality = logData.overall_quality_score_1024 || 0;
        const confidenceScore = logData.confidence_score_1024 || 0;
        const edgeQuality = logData.edge_quality_stats_1024?.quality_score || 0;

        return (
            <Box sx={{ mt: 1 }}>
                <Grid container spacing={1}>
                    {/* Key Quality Metrics */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main, fontSize: '0.875rem' }}>
                                🎯 Quality Metrics
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 'bold', 
                                    color: getQualityColor(overallQuality),
                                    mr: 1
                                }}>
                                    {(overallQuality * 100).toFixed(1)}%
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                    {getQualityLabel(overallQuality)}
                                </Typography>
                            </Box>
                            {renderProgressBar(
                                logData.transparency_stats_1024?.transparent_ratio || 0, 
                                1, 
                                "Transparent", 
                                '#4caf50',
                                `Transparent Pixels: ${((logData.transparency_stats_1024?.transparent_ratio || 0) * 100).toFixed(1)}%`
                            )}
                            {renderProgressBar(
                                logData.transparency_stats_1024?.opaque_ratio || 0, 
                                1, 
                                "Opaque", 
                                '#ff9800',
                                `Opaque Pixels: ${((logData.transparency_stats_1024?.opaque_ratio || 0) * 100).toFixed(1)}%`
                            )}
                            {renderProgressBar(
                                logData.transparency_stats_1024?.semi_transparent_ratio || 0, 
                                1, 
                                "Semi-Transparent", 
                                '#2196f3',
                                `Semi-Transparent Pixels: ${((logData.transparency_stats_1024?.semi_transparent_ratio || 0) * 100).toFixed(1)}%`
                            )}
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                                💡 Pixel transparency distribution
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Performance & Processing */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main, fontSize: '0.875rem' }}>
                                ⚡ Performance
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Total Time:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {totalTime}ms
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>GPU Used:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: logData.gpu_acceleration_used ? '#4caf50' : '#f44336' }}>
                                        {logData.gpu_acceleration_used ? 'Yes' : 'No'}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Content Retained:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {((logData.content_retention_ratio || 0) * 100).toFixed(1)}%
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Transparency & Defects */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main, fontSize: '0.875rem' }}>
                                🌫️ Transparency
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Transparent:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {((logData.transparency_stats_1024?.transparent_ratio || 0) * 100).toFixed(1)}%
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Opaque:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {((logData.transparency_stats_1024?.opaque_ratio || 0) * 100).toFixed(1)}%
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Avg Alpha:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {logData.weighted_transparency_stats_1024?.average_alpha ? 
                                            logData.weighted_transparency_stats_1024.average_alpha.toFixed(0) : 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    {/* Defects & Retry Info */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main, fontSize: '0.875rem' }}>
                                🔍 Issues & Retry
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Defects:</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {logData.defects_detected_1024 && Object.entries(logData.defects_detected_1024).map(([key, value]) => (
                                            value && (
                                                <Tooltip key={key} title={`${key.replace(/_/g, ' ')} detected`} arrow>
                                                    <Chip 
                                                        label={key.replace(/_/g, ' ').slice(0, 3)} 
                                                        color="error" 
                                                        size="small"
                                                        sx={{ height: '16px', fontSize: '0.6rem' }}
                                                    />
                                                </Tooltip>
                                            )
                                        ))}
                                        {(!logData.defects_detected_1024 || Object.values(logData.defects_detected_1024).every(v => !v)) && (
                                            <Chip 
                                                label="None" 
                                                color="success" 
                                                size="small"
                                                sx={{ height: '16px', fontSize: '0.6rem' }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Retry:</Typography>
                                    <Typography variant="body2" sx={{ 
                                        fontWeight: 'bold', 
                                        fontSize: '0.75rem',
                                        color: logData.retry_performed ? '#ff9800' : '#4caf50'
                                    }}>
                                        {logData.retry_performed ? 'Yes' : 'No'}
                                    </Typography>
                                </Box>
                                {logData.retry_performed && logData.retry_stats && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Improvement:</Typography>
                                        <Typography variant="body2" sx={{ 
                                            fontWeight: 'bold', 
                                            fontSize: '0.75rem', 
                                            color: (logData.retry_stats.improvement || 0) > 0 ? '#4caf50' : '#f44336'
                                        }}>
                                            {((logData.retry_stats.improvement || 0) * 100).toFixed(1)}%
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    const renderDetailedLogData = (logData: any) => {
        if (!logData) return null;

        const renderProgressBar = (value: number, max: number = 1, label: string, color?: string, tooltip?: string) => (
            <Tooltip title={tooltip || `${label}: ${((value || 0) * 100).toFixed(1)}%`} arrow>
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {((value || 0) * 100).toFixed(1)}%
                        </Typography>
                    </Box>
                    <Box sx={{
                        width: '100%',
                        height: '8px',
                        bgcolor: theme.palette.action.hover,
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            width: `${((value || 0) / max) * 100}%`,
                            height: '100%',
                            bgcolor: color || getQualityColor(value || 0),
                            transition: 'width 0.3s ease'
                        }} />
                    </Box>
                </Box>
            </Tooltip>
        );

        const renderMetricCard = (title: string, metrics: Array<{ label: string, value: any, unit?: string, color?: string }>) => (
            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    {title}
                </Typography>
                <Grid container spacing={1}>
                    {metrics.map((metric, index) => (
                        <Grid item xs={6} key={index}>
                            <Box sx={{ 
                                p: 1, 
                                bgcolor: theme.palette.background.paper, 
                                borderRadius: 1, 
                                border: `1px solid ${theme.palette.divider}`,
                                textAlign: 'center'
                            }}>
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                                    {metric.label}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    fontWeight: 'bold', 
                                    color: metric.color || theme.palette.text.primary
                                }}>
                                    {typeof metric.value === 'number' ? metric.value.toFixed(2) : metric.value}
                                    {metric.unit && ` ${metric.unit}`}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );

        return (
            <Box sx={{ mt: 2 }}>
                {/* System Information */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 1 }}>
                        🖥️ System Configuration
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            {renderMetricCard("Hardware & Model", [
                                { label: "Model Used", value: logData.model_used || 'N/A' },
                                { label: "Device", value: logData.device_used || 'N/A' },
                                { label: "GPU Available", value: logData.gpu_available ? 'Yes' : 'No', color: logData.gpu_available ? '#4caf50' : '#f44336' },
                                { label: "GPU Acceleration", value: logData.gpu_acceleration_used ? 'Enabled' : 'Disabled', color: logData.gpu_acceleration_used ? '#4caf50' : '#f44336' }
                            ])}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {renderMetricCard("Image Processing", [
                                { label: "Original Size", value: logData.original_size_wh ? `${logData.original_size_wh[0]}×${logData.original_size_wh[1]}` : 'N/A' },
                                { label: "Final Size", value: logData.final_size_wh ? `${logData.final_size_wh[0]}×${logData.final_size_wh[1]}` : 'N/A' },
                                { label: "Output Format", value: logData.output_format || 'N/A' },
                                { label: "Preview Mode", value: logData.preview_mode ? 'Yes' : 'No' }
                            ])}
                        </Grid>
                    </Grid>
                </Box>

                {/* Performance Metrics */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 1 }}>
                        ⚡ Performance Metrics
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            {(() => {
                                const processingTimes = calculateProcessingTimes(logData);
                                const cardTitle = logData.retry_performed ? "Processing Times (ms) - Retry" : "Processing Times (ms)";
                                return renderMetricCard(cardTitle, [
                                    { label: "Preprocess", value: processingTimes.preprocess, unit: 'ms' },
                                    { label: "Analysis", value: processingTimes.analysis, unit: 'ms' },
                                    { label: "BG Removal", value: processingTimes.bgRemoval, unit: 'ms' },
                                    { label: "Postprocess", value: processingTimes.postprocess, unit: 'ms' }
                                ]);
                            })()}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1, height: '100%' }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    Content Analysis
                                </Typography>
                                {renderProgressBar(logData.content_retention_ratio || 0, 1, "Content Retention")}
                                <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                                    {logData.content_retention_ratio ? 
                                        `${((logData.content_retention_ratio || 0) * 100).toFixed(1)}% of original content preserved` : 
                                        'No content retention data'
                                    }
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Quality Assessment */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 1 }}>
                        🎯 Quality Assessment
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    Overall Quality Score
                                </Typography>
                                <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <Typography variant="h4" sx={{ 
                                        fontWeight: 'bold', 
                                        color: getQualityColor(logData.overall_quality_score_1024 || 0)
                                    }}>
                                        {((logData.overall_quality_score_1024 || 0) * 100).toFixed(1)}%
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        {getQualityLabel(logData.overall_quality_score_1024 || 0)} Quality
                                    </Typography>
                                </Box>
                                {renderProgressBar(
                                    logData.overall_quality_score_1024 || 0, 
                                    1, 
                                    "Overall Quality", 
                                    getQualityColor(logData.overall_quality_score_1024 || 0),
                                    "Combined quality score based on multiple factors"
                                )}
                                {renderProgressBar(
                                    logData.confidence_score_1024 || 0, 
                                    1, 
                                    "Confidence Score", 
                                    getQualityColor(logData.confidence_score_1024 || 0),
                                    "AI model's confidence in the background removal accuracy"
                                )}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    Edge Quality Analysis
                                </Typography>
                                
                                {/* Розрахований Edge Quality */}
                                {(() => {
                                    const calculatedEdgeQuality = calculateEdgeQuality(logData);
                                    const apiEdgeQuality = logData.edge_quality_stats_1024?.quality_score || 0;
                                    const difference = calculatedEdgeQuality - apiEdgeQuality;
                                    
                                    return (
                                        <Box sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Calculated Edge Quality</Typography>
                                                <Typography variant="body2" sx={{ 
                                                    fontWeight: 'bold', 
                                                    color: getQualityColor(calculatedEdgeQuality),
                                                    fontSize: '1.1rem'
                                                }}>
                                                    {(calculatedEdgeQuality * 100).toFixed(1)}%
                                                </Typography>
                                            </Box>
                                            {renderProgressBar(
                                                calculatedEdgeQuality, 
                                                1, 
                                                "Calculated", 
                                                getQualityColor(calculatedEdgeQuality),
                                                `Calculated Edge Quality: ${(calculatedEdgeQuality * 100).toFixed(1)}% - ${getQualityLabel(calculatedEdgeQuality)}`
                                            )}
                                            
                                            {/* Порівняння з API */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 1 }}>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>API Score</Typography>
                                                <Typography variant="body2" sx={{ 
                                                    fontSize: '0.8rem', 
                                                    color: getQualityColor(apiEdgeQuality),
                                                    fontWeight: 'bold'
                                                }}>
                                                    {(apiEdgeQuality * 100).toFixed(1)}%
                                                </Typography>
                                            </Box>
                                            {renderProgressBar(
                                                apiEdgeQuality, 
                                                1, 
                                                "API", 
                                                getQualityColor(apiEdgeQuality),
                                                `API Edge Quality: ${(apiEdgeQuality * 100).toFixed(1)}% - ${getQualityLabel(apiEdgeQuality)}`
                                            )}
                                            

                                        </Box>
                                    );
                                })()}
                                
                                {/* Детальні метрики */}
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1, fontWeight: 'bold' }}>
                                    Detailed Metrics:
                                </Typography>
                                {renderProgressBar(
                                    logData.edge_quality_stats_1024?.clean_edges_ratio || 0, 
                                    1, 
                                    "Clean Edges Ratio", 
                                    getQualityColor(logData.edge_quality_stats_1024?.clean_edges_ratio || 0),
                                    "Percentage of clean, artifact-free edges"
                                )}
                                {renderProgressBar(
                                    logData.edge_quality_stats_1024?.dark_artifacts_ratio || 0, 
                                    1, 
                                    "Dark Artifacts Ratio", 
                                    getQualityColor(1 - (logData.edge_quality_stats_1024?.dark_artifacts_ratio || 0)),
                                    "Percentage of dark artifacts around edges (lower is better)"
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Transparency Analysis */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 1 }}>
                        🌫️ Transparency Analysis
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            {renderMetricCard("Pixel Distribution", [
                                { label: "Total Pixels", value: logData.transparency_stats_1024?.total_pixels?.toLocaleString() || 'N/A' },
                                { label: "Fully Transparent", value: logData.transparency_stats_1024?.fully_transparent?.toLocaleString() || 'N/A' },
                                { label: "Semi Transparent", value: logData.transparency_stats_1024?.semi_transparent?.toLocaleString() || 'N/A' },
                                { label: "Fully Opaque", value: logData.transparency_stats_1024?.fully_opaque?.toLocaleString() || 'N/A' }
                            ])}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    Transparency Ratios
                                </Typography>
                                {renderProgressBar(logData.transparency_stats_1024?.transparent_ratio || 0, 1, "Transparent Pixels", '#4caf50')}
                                {renderProgressBar(logData.transparency_stats_1024?.semi_transparent_ratio || 0, 1, "Semi-Transparent Pixels", '#ff9800')}
                                {renderProgressBar(logData.transparency_stats_1024?.opaque_ratio || 0, 1, "Opaque Pixels", '#2196f3')}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Defects Detection */}
                {logData.defects_detected_1024 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 1 }}>
                            🔍 Defects Detection
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Detected Issues
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {Object.entries(logData.defects_detected_1024).map(([key, value]) => (
                                            <Box key={key} sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                p: 1,
                                                bgcolor: theme.palette.background.paper,
                                                borderRadius: 1,
                                                border: `1px solid ${theme.palette.divider}`
                                            }}>
                                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                                    {key.replace(/_/g, ' ')}
                                                </Typography>
                                                <Chip 
                                                    label={value ? 'Detected' : 'None'} 
                                                    color={value ? 'error' : 'success'} 
                                                    size="small"
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Enhanced Edge Quality Components
                                    </Typography>
                                    {logData.enhanced_edge_quality_1024 && (
                                        <>
                                            {renderProgressBar(logData.enhanced_edge_quality_1024.edge_sharpness || 0, 1, "Edge Sharpness (25%)", getQualityColor(logData.enhanced_edge_quality_1024.edge_sharpness || 0), `Edge Sharpness: ${((logData.enhanced_edge_quality_1024.edge_sharpness || 0) * 100).toFixed(1)}% - Weight: 25%`)}
                                            {renderProgressBar(logData.enhanced_edge_quality_1024.color_consistency_score || 0, 1, "Color Consistency (20%)", getQualityColor(logData.enhanced_edge_quality_1024.color_consistency_score || 0), `Color Consistency: ${((logData.enhanced_edge_quality_1024.color_consistency_score || 0) * 100).toFixed(1)}% - Weight: 20%`)}
                                            {renderProgressBar(logData.enhanced_edge_quality_1024.morphological_cleanness || 0, 1, "Morphological Cleanliness (20%)", getQualityColor(logData.enhanced_edge_quality_1024.morphological_cleanness || 0), `Morphological Cleanliness: ${((logData.enhanced_edge_quality_1024.morphological_cleanness || 0) * 100).toFixed(1)}% - Weight: 20%`)}
                                            {renderProgressBar(logData.enhanced_edge_quality_1024.canny_edge_density || 0, 1, "Edge Density (15%)", getQualityColor(logData.enhanced_edge_quality_1024.canny_edge_density || 0), `Canny Edge Density: ${((logData.enhanced_edge_quality_1024.canny_edge_density || 0) * 100).toFixed(1)}% - Weight: 15%`)}
                                            {renderProgressBar(Math.min((logData.enhanced_edge_quality_1024.sobel_edge_strength || 0) / 30, 1), 1, "Edge Strength (10%)", getQualityColor(Math.min((logData.enhanced_edge_quality_1024.sobel_edge_strength || 0) / 30, 1)), `Sobel Edge Strength: ${(logData.enhanced_edge_quality_1024.sobel_edge_strength || 0).toFixed(1)} (normalized) - Weight: 10%`)}
                                            {renderProgressBar(logData.enhanced_edge_quality_1024.weighted_artifact_score || 0, 1, "Artifact Score (10%)", getQualityColor(logData.enhanced_edge_quality_1024.weighted_artifact_score || 0), `Weighted Artifact Score: ${((logData.enhanced_edge_quality_1024.weighted_artifact_score || 0) * 100).toFixed(1)}% - Weight: 10%`)}
                                            
                                            {/* Штраф за протікання кольору */}
                                            <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
                                                <Typography variant="caption" sx={{ color: '#856404', fontWeight: 'bold' }}>
                                                    Color Bleeding Penalty: -{((logData.enhanced_edge_quality_1024.color_bleeding_ratio || 0) * 30).toFixed(1)}%
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#856404', display: 'block' }}>
                                                    (Color Bleeding Ratio: {((logData.enhanced_edge_quality_1024.color_bleeding_ratio || 0) * 100).toFixed(1)}%)
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Retry Information */}
                {logData.retry_performed && logData.retry_stats && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, borderBottom: `2px solid ${theme.palette.primary.main}`, pb: 1 }}>
                            🔄 Retry Analysis
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#e65100' }}>
                                        Retry Reason
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                                        {logData.retry_stats.retry_reason}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderMetricCard("Improvement Metrics", [
                                    { label: "Improvement", value: ((logData.retry_stats.improvement || 0) * 100).toFixed(1), unit: '%', color: (logData.retry_stats.improvement || 0) > 0 ? '#4caf50' : '#f44336' },
                                    { label: "Retry Time", value: logData.retry_stats.retry_time_ms || 0, unit: 'ms' },
                                    { label: "Better Result", value: logData.retry_stats.better_result || 'N/A' },
                                    { label: "Hybrid Applied", value: logData.retry_stats.hybrid_improvement_applied ? 'Yes' : 'No', color: logData.retry_stats.hybrid_improvement_applied ? '#4caf50' : '#f44336' }
                                ])}
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Original vs Retry Comparison
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Original Alpha: {(logData.retry_stats.original_avg_alpha || 0).toFixed(1)}</Typography>
                                        <Typography variant="body2">Retry Alpha: {(logData.retry_stats.retry_avg_alpha || 0).toFixed(1)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Original Score: {((logData.retry_stats.original_visual_score || 0) * 100).toFixed(1)}%</Typography>
                                        <Typography variant="body2">Retry Score: {((logData.retry_stats.retry_visual_score || 0) * 100).toFixed(1)}%</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">Original Edge: {((logData.retry_stats.original_edge_quality || 0) * 100).toFixed(1)}%</Typography>
                                        <Typography variant="body2">Retry Edge: {((logData.retry_stats.retry_edge_quality || 0) * 100).toFixed(1)}%</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Transparency Improvement
                                    </Typography>
                                    {renderProgressBar(logData.retry_stats.original_weighted_transparency || 0, 1, "Original Transparency", '#f44336')}
                                    {renderProgressBar(logData.retry_stats.retry_weighted_transparency || 0, 1, "Retry Transparency", '#4caf50')}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell>{log.eventId}</TableCell>
                <TableCell>
                    <Chip 
                        label={log.status} 
                        color={getStatusColor(log.status) as any}
                        size="small"
                    />
                </TableCell>
                <TableCell>{formatResolution(log.targetResolution)}</TableCell>
                <TableCell>
                    <Chip 
                        label={log.isRetryExist ? 'Yes' : 'No'} 
                        color={log.isRetryExist ? 'warning' : 'default'}
                        size="small"
                    />
                </TableCell>
                <TableCell align="center">{formatDuration(log.mockupDuration)}</TableCell>
                <TableCell>{formatDuration(log.totalDurationMs)}</TableCell>
                <TableCell>{formatDuration(log.inferenceMs)}</TableCell>
                <TableCell>{formatDuration(log.postprocessMs)}</TableCell>
                <TableCell>
                    {dayjs(log.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Event Details
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                <Box sx={{ flex: 1, minWidth: '250px' }}>
                                    <Typography variant="subtitle2" gutterBottom>Basic Information</Typography>
                                    <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1, height: '100%' }}>
                                        <Typography variant="body2"><strong>Customer ID:</strong> {log.customerId}</Typography>
                                        <Typography variant="body2"><strong>Session ID:</strong> {log.sessionId}</Typography>
                                        <Typography variant="body2"><strong>IP:</strong> {log.ip}</Typography>
                                        <Typography variant="body2"><strong>Image Type:</strong> {log.imageType}</Typography>
                                        <Typography variant="body2"><strong>To Cart:</strong> {log.toCart ? 'Yes' : 'No'}</Typography>
                                        <Typography variant="body2"><strong>Order Exist:</strong> {log.orderExist ? 'Yes' : 'No'}</Typography>
                                        <Typography variant="body2"><strong>Skip Save:</strong> {log.skipSave ? 'Yes' : 'No'}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: '250px' }}>
                                    <Typography variant="subtitle2" gutterBottom>Performance Metrics</Typography>
                                    <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1, height: '100%' }}>
                                        <Typography variant="body2"><strong>Init Mockup Delay:</strong> {log.initMockupDelay}s</Typography>
                                        <Typography variant="body2"><strong>Process Duration:</strong> {log.processDuration}s</Typography>
                                        <Typography variant="body2"><strong>Mockup Duration:</strong> {log.mockupDuration}s</Typography>
                                        <Typography variant="body2"><strong>Actual Mockup Duration:</strong> {log.actualMockupDuration}s</Typography>
                                        <Typography variant="body2"><strong>Save Duration:</strong> {log.saveDuration}s</Typography>
                                        <Typography variant="body2"><strong>Additional Events:</strong> {log.additionalEvents}</Typography>
                                    </Box>
                                </Box>
                                {log.imageUrl && (
                                    <Box sx={{ flex: 1, minWidth: '250px' }}>
                                        <Typography variant="subtitle2" gutterBottom>Original Image</Typography>
                                        <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1, height: '100%' }}>
                                            <a href={log.imageUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                <img 
                                                    src={log.imageUrl} 
                                                    alt="Original Image" 
                                                    style={{ 
                                                        width: '100%', 
                                                        maxHeight: '150px', 
                                                        objectFit: 'contain',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px'
                                                    }}
                                                />
                                            </a>
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <a href={log.imageUrl} download style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                                                    Download Original Image
                                                </a>
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                {log.mockupUrl && (
                                    <Box sx={{ flex: 1, minWidth: '250px' }}>
                                        <Typography variant="subtitle2" gutterBottom>Mockup Image</Typography>
                                        <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 1, height: '100%' }}>
                                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                <img 
                                                    src={log.mockupUrl} 
                                                    alt="Mockup Image" 
                                                    style={{ 
                                                        width: '100%', 
                                                        maxHeight: '150px', 
                                                        objectFit: 'contain',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px'
                                                    }}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={handleMagnifierClick}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        right: '8px',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 1)',
                                                        },
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                    }}
                                                >
                                                    <ZoomIn fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                <a href={log.mockupUrl} download style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                                                    Download Mockup Image
                                                </a>
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            {log.errorMessage && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom color="error">Error Information</Typography>
                                    <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                                        <Typography variant="body2" color="error"><strong>Error:</strong> {log.errorMessage}</Typography>
                                        {log.errorMessageDetailed && (
                                            <Typography variant="body2" color="error"><strong>Details:</strong> {log.errorMessageDetailed}</Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                            {log.log && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, paddingTop:'20px' }}>
                                        <Typography variant="subtitle2" gutterBottom>RemoveBG Log Analysis</Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Analytics />}
                                                onClick={() => setDetailedLogOpen(true)}
                                                sx={{ fontSize: '0.75rem' }}
                                            >
                                                Detailed Analysis
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<Code />}
                                                onClick={() => setRawDataOpen(true)}
                                                sx={{ fontSize: '0.75rem' }}
                                            >
                                                Raw Data
                                            </Button>
                                        </Box>
                                    </Box>
                                    {renderCompactLogData(log.log)}
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            
            {/* Magnifier Popover */}
            <Popover
                open={magnifierOpen}
                anchorEl={magnifierAnchor}
                onClose={handleMagnifierClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ p: 1, bgcolor: theme.palette.action.hover }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Hover over image to zoom
                    </Typography>
                </Box>
                <Box sx={{ position: 'relative', width: '400px', height: '300px', overflow: 'hidden' }}>
                    <img 
                        src={log.mockupUrl} 
                        alt="Mockup Image Magnified" 
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            cursor: 'crosshair'
                        }}
                        onMouseMove={handleImageMouseMove}
                    />
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: `url(${log.mockupUrl})`,
                        backgroundSize: '400% 400%',
                        backgroundPosition: `${mousePosition.x}% ${mousePosition.y}%`,
                        pointerEvents: 'none',
                        zIndex: 1
                    }} />
                </Box>
            </Popover>

            {/* Detailed Log Modal */}
            <Modal
                open={detailedLogOpen}
                onClose={() => setDetailedLogOpen(false)}
                aria-labelledby="detailed-log-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Box sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    width: '1200px'
                }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h5" component="h2" sx={{ color: theme.palette.primary.main }}>
                                        Detailed RemoveBG Analysis - Event {log.eventId}
                                    </Typography>
                                    <IconButton onClick={() => setDetailedLogOpen(false)}>
                                        <KeyboardArrowUp />
                                    </IconButton>
                                </Box>
                                {log.log && renderDetailedLogData(log.log)}
                            </Box>
                        </Modal>

                        {/* Raw Data Modal */}
                        <Modal
                            open={rawDataOpen}
                            onClose={() => setRawDataOpen(false)}
                            aria-labelledby="raw-data-modal"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 2
                            }}
                        >
                            <Box sx={{
                                bgcolor: 'background.paper',
                                borderRadius: 2,
                                boxShadow: 24,
                                p: 3,
                                maxWidth: '95vw',
                                maxHeight: '95vh',
                                overflow: 'auto',
                                width: '1400px'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h5" component="h2" sx={{ color: theme.palette.primary.main }}>
                                        Raw Event Data - Event {log.eventId}
                                    </Typography>
                                    <IconButton onClick={() => setRawDataOpen(false)}>
                                        <KeyboardArrowUp />
                                    </IconButton>
                                </Box>
                                <Box sx={{ 
                                    bgcolor: theme.palette.action.hover, 
                                    p: 2, 
                                    borderRadius: 1, 
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap',
                                    overflow: 'auto',
                                    maxHeight: '80vh'
                                }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        Complete Event Data (JSON):
                                    </Typography>
                                    <pre style={{ margin: 0, color: theme.palette.text.primary }}>
                                        {JSON.stringify(log, null, 2)}
                                    </pre>
                                </Box>
                            </Box>
                        </Modal>
                    </>
                );
            }

interface RemovebgLogsTableProps {
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export default function RemovebgLogsTable({ onPageChange, onRowsPerPageChange }: RemovebgLogsTableProps) {
    const { removebgLogs, status, queryParams } = useSelector<IStore, { 
        removebgLogs: any, 
        status: string, 
        queryParams: any 
    }>(state => ({
        removebgLogs: state.removebgLogs.data,
        status: state.removebgLogs.status,
        queryParams: state.removebgLogs.queryParams
    }));

    console.log('RemovebgLogsTable render:', { removebgLogs, status, queryParams });

    const classes = useStyles();

    if (status === 'loading') {
        return <div>Loading...</div>;
    }

    if (status === 'failed') {
        return <div>Error loading data</div>;
    }

    const logs = removebgLogs?.result || [];
    const total = removebgLogs?.totalCount || logs.length;
    const currentPage = (queryParams?.page || 1) - 1;
    const rowsPerPage = queryParams?.limit || 100;

    console.log('TablePagination props:', { total, currentPage, rowsPerPage, queryParams });

    return (
        <Paper className={classes.tableContainer}>
            <TableContainer>
                <Table stickyHeader aria-label="removebg logs table">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Event ID</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Resolution</TableCell>
                            <TableCell>Retry</TableCell>
                            <TableCell align="center">Mockup Duration</TableCell>
                            <TableCell>Total Duration</TableCell>
                            <TableCell>Inference</TableCell>
                            <TableCell>Postprocess</TableCell>
                            <TableCell>Created At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map((log: RemovebgLog) => (
                            <Row key={log.eventId} log={log} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[50, 100, 200, 500]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
        </Paper>
    );
} 